import { LOG } from 'src/app/common/util/log';

import { FlatTableSorter, GroupedTableSorter, RowCompareFn } from '../table-sorter';
import { CellContent, Table, TableColumnMetadata, TableRow, TableSorter } from '../types';
import { isInstanceOfCellContentObject, isInstanceOfGroupingDefsCell } from '../util';
import { isSpecialHeader } from './special-headers';
import { TableMetadataHandler } from './table-metadata-handler';

export class TableRowSortHandler {
  private primaryColumn: number = -1;

  public setAsPrimaryColumn(column: number): void {
    this.primaryColumn = column;
  }

  public buildTableSorter(table: Table, metaHandler: TableMetadataHandler): TableSorter {
    const compareFns = this.buildCompareFunctions(table, metaHandler);

    if (this.primaryColumn < 0) {
      return new FlatTableSorter(table, compareFns);
    }

    const rowGroups: Map<unknown, TableRow[]> = new Map();

    for (const row of table.rows) {
      const value = row.content[this.primaryColumn];
      let key: unknown;

      if (isInstanceOfGroupingDefsCell(value)) {
        key = value.group;
      } else {
        key = value;
      }

      let rowGroup = rowGroups.get(key);

      if (!rowGroup) {
        rowGroup = [];
        rowGroups.set(key, rowGroup);
      }

      rowGroup.push(row);
    }

    const index = this.primaryColumn;
    const findHeadFn = (row: TableRow): boolean => {
      const value = row.content[index];
      if (!value || !value['isHeader']) {
        return false;
      }

      return value['isHeader'] === true;
    };

    return new GroupedTableSorter(table, Array.from(rowGroups.values()), compareFns, findHeadFn);
  }

  private buildCompareFunctions(table: Table, metaHandler: TableMetadataHandler): Map<number, RowCompareFn> {
    const compareFns: Map<number, RowCompareFn> = new Map();

    const headers = table.headers.flat();

    for (const header of headers) {
      if (!header || isSpecialHeader(header) || table.meta[header.column]?.unsortable) {
        continue;
      }

      const columnIndex = header.column;
      const cellContent: CellContent | null = this.getFirstNonNullCellContentOrNull(table, columnIndex);

      if (cellContent === null) {
        LOG.error(`Column ${header} has no values and must be marked as unsortable`);
        metaHandler.addUnsortable(columnIndex);
        continue;
      }

      const compareFn: RowCompareFn = this.getCompareFnForColumn(cellContent, columnIndex, metaHandler.metadata?.[columnIndex]);

      if (!compareFn) {
        LOG.error(`Cant compare type: ${JSON.stringify(cellContent)}`);
        metaHandler.addUnsortable(header.column);
        continue;
      }

      compareFns.set(header.column, compareFn);
    }

    return compareFns;
  }

  private getFirstNonNullCellContentOrNull(table: Table, columnIndex: number): CellContent | null {
    for (const row of table.rows) {
      if (row.meta?.isGroupHeader) {
        continue;
      }

      const current = row.content[columnIndex];

      if (current === undefined || current === null) {
        continue;
      }

      return current;
    }

    return null;
  }

  private getCompareFnForColumn(cellContent: CellContent, columnIndex: number, meta?: TableColumnMetadata): RowCompareFn | undefined {
    if (typeof cellContent === 'string') {
      return this.getStringCompareFunction(row => row.content[columnIndex] as string);
    }

    if (typeof cellContent === 'number') {
      return this.getNumberCompareFunction(row => row.content[columnIndex] as number);
    }

    if (isInstanceOfCellContentObject(cellContent)) {
      const cellValue = cellContent.value;
      const sortKey = meta?.sortBy || 'value';

      if (typeof cellValue === 'string') {
        return this.getStringCompareFunction(row => row.content[columnIndex][sortKey]);
      }

      if (typeof cellValue === 'number') {
        return this.getNumberCompareFunction(row => row.content[columnIndex][sortKey]);
      }
    }

    return undefined;
  }

  private getStringCompareFunction(getValue: (row: TableRow) => string): RowCompareFn {
    return (a, b) => (getValue(a) || '').localeCompare(getValue(b) || '');
  }

  private getNumberCompareFunction(getValue: (row: TableRow) => number): RowCompareFn {
    return (a, b) => getValue(a) - getValue(b);
  }
}
